
import React from 'react';

interface RadarChartProps {
  data: {
    label: string;
    value: number; // 0-100
  }[];
  size?: number;
  color?: string;
}

export const RadarChart: React.FC<RadarChartProps> = ({ data, size = 300, color = '#4F46E5' }) => {
  const count = data.length;
  const radius = (size / 2) - 40; // Padding for labels
  const center = size / 2;
  const angleStep = (Math.PI * 2) / count;

  // Helper to calculate coordinates
  const getCoordinates = (value: number, index: number) => {
    const angle = index * angleStep - Math.PI / 2; // Start at top
    // Scale value (0-100) to radius
    const r = (value / 100) * radius;
    const x = center + r * Math.cos(angle);
    const y = center + r * Math.sin(angle);
    return { x, y };
  };

  // Generate points for the data polygon
  const points = data.map((d, i) => {
    const { x, y } = getCoordinates(d.value, i);
    return `${x},${y}`;
  }).join(' ');

  // Generate grid levels (20%, 40%, 60%, 80%, 100%)
  const levels = [20, 40, 60, 80, 100];

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background Grid (Web) */}
      {levels.map((level) => {
        const levelPoints = data.map((_, i) => {
          const { x, y } = getCoordinates(level, i);
          return `${x},${y}`;
        }).join(' ');
        return (
          <polygon
            key={level}
            points={levelPoints}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="1"
          />
        );
      })}

      {/* Axis Lines */}
      {data.map((_, i) => {
        const { x, y } = getCoordinates(100, i);
        return (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={x}
            y2={y}
            stroke="#E5E7EB"
            strokeWidth="1"
          />
        );
      })}

      {/* Data Polygon */}
      <polygon
        points={points}
        fill={color}
        fillOpacity="0.2"
        stroke={color}
        strokeWidth="2"
      />

      {/* Data Points */}
      {data.map((d, i) => {
        const { x, y } = getCoordinates(d.value, i);
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="4"
            fill={color}
          />
        );
      })}

      {/* Labels */}
      {data.map((d, i) => {
        // Push labels out slightly further than 100% radius
        const angle = i * angleStep - Math.PI / 2;
        const labelRadius = radius + 25;
        const x = center + labelRadius * Math.cos(angle);
        const y = center + labelRadius * Math.sin(angle);
        
        // Anchor text based on position
        let anchor: "middle" | "start" | "end" = 'middle';
        if (x < center - 10) anchor = 'end';
        if (x > center + 10) anchor = 'start';

        return (
          <text
            key={i}
            x={x}
            y={y}
            textAnchor={anchor}
            dominantBaseline="middle"
            className="text-[10px] sm:text-xs fill-gray-500 font-medium uppercase"
            style={{ fontSize: '10px' }}
          >
            {d.label}
          </text>
        );
      })}
    </svg>
  );
};
